import { useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Star, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Eyebrow from './Eyebrow';
import { api } from '../api';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

const STATIC_TESTIMONIALS = [
  { quote: "Syenovix didn't just build us software — they transformed how we think about our product. Their AI consulting work alone saved us two years of trial and error.", name: 'Sarah Chen', role: 'CTO', company: 'NovaPay · FinTech', initials: 'SC', hue: '#5E0ED7' },
  { quote: "The team's ability to blend deep technical expertise with business strategy is rare. They rebuilt our entire platform in 6 months without a single day of downtime.", name: 'Marcus Webb', role: 'CEO', company: 'DataStack Pro · SaaS', initials: 'MW', hue: '#18181B' },
  { quote: "Their UX redesign increased our conversion rate by 47% in the first month. They understand the user and the business side simultaneously.", name: 'Priya Nair', role: 'VP Product', company: 'HealthBridge · HealthTech', initials: 'PN', hue: '#3730a3' },
  { quote: "We brought them in to build an AI assistant. They delivered something that changed our entire operations model. Best technology investment we've made.", name: 'James Okafor', role: 'COO', company: 'Meridian · Logistics', initials: 'JO', hue: '#1e1b4b' },
  { quote: "Fast, professional, and genuinely invested in our success. Syenovix feels less like a vendor and more like a co-founder embedded in our team.", name: 'Elena Vasquez', role: 'Founder', company: 'Luma Commerce · E-comm', initials: 'EV', hue: '#4c1d95' },
  { quote: "Their process is unusually rigorous — discovery, architecture review, user research before a line of code. The result is software that actually works for our users.", name: 'Tom Richter', role: 'Head of Eng.', company: 'Buildio · PropTech', initials: 'TR', hue: '#2d1b69' },
];

function Stars() {
  return (
    <div className="flex gap-0.5 mb-5">
      {[...Array(5)].map((_, i) => <Star key={i} size={13} fill={ACCENT} style={{ color: ACCENT }} />)}
    </div>
  );
}

function TestimonialCard({ t, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.06, duration: 0.55, ease }}
      whileHover={{ y: -5, scale: 1.01 }}
      className="cursor-default h-full"
    >
      <div className="rounded-[1.75rem] overflow-hidden h-full" style={{ boxShadow: '0 4px 24px -6px rgba(0,0,0,0.10)' }}>
        {/* Colored top strip using hue */}
        <div className="h-1.5 w-full" style={{ background: t.hue }} />
        <div className="bg-white h-full p-7 flex flex-col">
          <Stars />
          <p className="text-sm leading-relaxed flex-1 mb-7" style={{ color: '#3f3f46' }}>"{t.quote}"</p>
          <div className="flex items-center gap-3 pt-4" style={{ borderTop: '1px solid rgba(24,24,27,0.06)' }}>
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
              style={{ background: t.hue }}
            >
              {t.initials}
            </div>
            <div>
              <div className="text-[13px] font-semibold" style={{ color: '#18181B' }}>{t.name}</div>
              <div className="text-[11px] mt-0.5" style={{ color: '#A0A0A7' }}>{t.role} · {t.company}</div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Testimonials() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const [testimonials, setTestimonials] = useState(STATIC_TESTIMONIALS);

  useEffect(() => {
    api.getTestimonials()
      .then(d => { if (d?.length) setTestimonials(d); })
      .catch(() => {});
  }, []);

  return (
    <section id="feedback" className="px-5 sm:px-8 md:px-12 lg:px-20 flex flex-col justify-center py-24 section-gray" style={{ minHeight: '100dvh' }}>
      <div className="max-w-7xl mx-auto">
        <div ref={ref} className="mb-14">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.45, ease }}>
            <Eyebrow>Client Feedback</Eyebrow>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.08, ease }}
            className="text-4xl md:text-5xl lg:text-[3.5rem] font-bold tracking-tight max-w-xl leading-[1.08]"
            style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.025em' }}
          >
            What Clients Say
          </motion.h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
          {testimonials.map((t, i) => <TestimonialCard key={t.name || t.id} t={t} index={i} />)}
        </div>

        {/* CTA — share your experience */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl p-8"
          style={{ background: 'rgba(94,14,215,0.05)', border: '1px solid rgba(94,14,215,0.12)' }}
        >
          <div>
            <p className="font-bold text-lg" style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Worked with us?
            </p>
            <p className="text-sm mt-0.5" style={{ color: '#6B6B72' }}>
              Share your experience — it helps others make confident decisions.
            </p>
          </div>
          <Link
            to="/testimonials/submit"
            className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white shrink-0"
            style={{ background: ACCENT }}
          >
            Write a Testimonial <ArrowUpRight size={14} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
